import { ArrowRight1Bold } from '@ha/icons';
import React from 'react';
import Helmet from 'react-helmet';
import { makeStyles } from '@material-ui/core';

import { Breadcrumbs as MuiBreadcrumbs } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import { notEmpty } from 'ha/utils/notEmpty';

import HousingAnywhereIcon from 'custom-icons/housing-anywhere-icon.svg';

import { Breadcrumb } from './types';
import BreadcrumbItem from './BreadcrumbItem';

export interface Props {
  breadcrumbs: Array<Breadcrumb | undefined>;
  withJsonLDSchema?: boolean;
}

const getJsonLdBreadcrumbs = (breadcrumbs: Breadcrumb[]) =>
  JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map(
      ({ name, schemaName = name, path, schemaPath = path }, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': schemaPath,
          name: schemaName,
        },
      }),
    ),
  });

export const useStyles = makeStyles({
  divider: {
    fontSize: 8,
  },
  ol: {
    whiteSpace: 'nowrap',
    flexWrap: 'initial',
  },
});

const Breadcrumbs: React.FC<Props> = ({
  breadcrumbs,
  withJsonLDSchema = true,
}) => {
  const filteredBreadcrumbs = breadcrumbs.filter(notEmpty);

  const { T, urlResolver } = useIntl();
  const classes = useStyles();

  return (
    <React.Fragment>
      {withJsonLDSchema && (
        <Helmet>
          <script type="application/ld+json" id="structuredDataBreadcrumbs">
            {getJsonLdBreadcrumbs([
              { name: T('Home'), path: urlResolver.getBaseUrl() },
              ...filteredBreadcrumbs,
            ])}
          </script>
        </Helmet>
      )}
      <MuiBreadcrumbs
        separator={<ArrowRight1Bold className={classes.divider} />}
        classes={{
          ol: classes.ol,
        }}
        data-test-locator="Breadcrumbs"
      >
        <BreadcrumbItem
          text={<HousingAnywhereIcon fontSize="inherit" />}
          url={urlResolver.getHomeUrl()}
          renderAsLink
        />
        {filteredBreadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem
            key={index}
            text={breadcrumb.name}
            url={breadcrumb.path}
            renderAsLink={index !== breadcrumbs.length - 1}
          />
        ))}
      </MuiBreadcrumbs>
    </React.Fragment>
  );
};

export default Breadcrumbs;
